import { QueryConfigTypes } from '@wix/premium-fed-sdk/@apis-infra';
import { CartApiTags } from '../premium-cart/premium-cart.types';
import { CartStatus } from '@wix/ambassador-premium-cart-v1-cart/types';
import { queryCartPricing } from '../../cart-api-query-functions/queryCartPricing';
import { PremiumCartApiConfig } from '../premium-cart/premium-cart.api-config';
import { CartApiClientUtils } from '../../utils/cart-api-client.utils';
import { ArrayUtils, EmptyCartError, CompletedCartError } from '@wix/premium-cart-common';
import { QueryUtils } from '../../../query.utils';
import { PremiumCartServerlessApiClient } from '../../../premium-cart-serverless-api-client';
import { CartEnrichers } from '../../enrichers';
import { upsertLineItems } from '../../services';
const service = {
  baseUrl: '',
  defaultHttpMethod: 'post',
  defaultQueryConfigType: QueryConfigTypes.DYNAMIC_DATA
};
export const PremiumCartApiAggregations = {
  getCurrentCart: {
    name: 'getCurrentCart',
    service,
    tags: [CartApiTags.cart],
    queryFn: async _ref => {
      var _activeCartState$cart, _activeCartState$cart2, _getCartRes$cart, _getCartRes$cart2, _getCartRes$cart3;
      let {
        httpClient
      } = _ref;
      const CartApiClient = await CartApiClientUtils.dynamicLoadCartApiClient();
      const {
        getActiveCart,
        getCart
      } = CartApiClient.queries({
        httpClient
      });
      const activeCartState = QueryUtils.getQueryFromCache(CartApiClient, PremiumCartApiConfig.getActiveCart.name);
      const getCartRes = activeCartState != null && (_activeCartState$cart = activeCartState.cart) != null && _activeCartState$cart.id ? await getCart({
        cartId: activeCartState == null || (_activeCartState$cart2 = activeCartState.cart) == null ? void 0 : _activeCartState$cart2.id
      }) : await getActiveCart({});
      if (!Array.isArray((_getCartRes$cart = getCartRes.cart) == null ? void 0 : _getCartRes$cart.lineItems) || ((_getCartRes$cart2 = getCartRes.cart) == null || (_getCartRes$cart2 = _getCartRes$cart2.lineItems) == null ? void 0 : _getCartRes$cart2.length) === 0) {
        throw new EmptyCartError(getCartRes);
      }
      if (((_getCartRes$cart3 = getCartRes.cart) == null ? void 0 : _getCartRes$cart3.status) === CartStatus.COMPLETED) {
        throw new CompletedCartError(getCartRes);
      }
      return getCartRes;
    }
  },
  getEnrichedCurrentCart: {
    name: 'getEnrichedCurrentCart',
    service,
    tags: [CartApiTags.cart],
    queryFn: async _ref2 => {
      let {
        httpClient
      } = _ref2;
      const {
        getCurrentCart,
        enrichCart
      } = await CartApiClientUtils.dynamicLoadCartQueries(httpClient);
      const {
        cart
      } = await getCurrentCart({});
      if (!cart) {
        // TODO - error handling
        throw new Error('Line items in cart is undefined');
      }
      const {
        cart: enrichedCart
      } = await enrichCart({
        cart
      });
      // TODO - error handling

      return {
        cart: enrichedCart
      };
    }
  },
  enrichCart: {
    name: 'enrichCart',
    service,
    queryConfigType: QueryConfigTypes.FIXED_DATA,
    queryFn: async _ref3 => {
      let {
        httpClient,
        request
      } = _ref3;
      const cart = request.cart;
      if (!Array.isArray(cart == null ? void 0 : cart.lineItems)) {
        // TODO - error handling
        throw new Error('Line items in cart is undefined');
      }
      const productTypeIds = ArrayUtils.safeFilter(cart.lineItems.map(lineItem => {
        var _lineItem$productInfo;
        return (_lineItem$productInfo = lineItem.productInfo) == null ? void 0 : _lineItem$productInfo.productTypeId;
      }), productTypeId => Boolean(productTypeId));
      const {
        productTypesDetails
      } = await PremiumCartServerlessApiClient.queries({
        httpClient
      }).listProductTypeDetails({
        productTypeIds,
        msid: cart.metasiteId
      });
      if (!productTypesDetails) {
        // TODO - change to technical error
        throw new EmptyCartError({});
      }
      const {
        cart: enrichedCart
      } = CartEnrichers.enrichCart({
        cart,
        productTypesDetails
      });
      return {
        cart: enrichedCart
      };
    }
  },
  updateLineItems: {
    name: 'updateLineItems',
    service,
    tags: [CartApiTags.cart, CartApiTags.updateLineItems],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref4 => {
      let {
        httpClient,
        request
      } = _ref4;
      const {
        lineItems: enrichedLineItems
      } = request;
      const CartApiClient = await CartApiClientUtils.dynamicLoadCartApiClient();
      const {
        getCurrentCart,
        enrichCart,
        ambassadorUpdateLineItems
      } = CartApiClient.queries({
        httpClient
      });
      const {
        cart
      } = await getCurrentCart({});
      if (!(cart != null && cart.id) || !cart.revision) {
        // TODO - error handling
        throw new Error('updateLineItems request params error');
      }
      const updatedCart = await ambassadorUpdateLineItems({
        cartId: cart.id,
        revision: cart.revision,
        lineItems: Object.values(enrichedLineItems).map(enrichedLineItem => {
          var _enrichedLineItem$pro;
          return {
            lineItemId: enrichedLineItem.id,
            cycle: enrichedLineItem.cycle,
            metadata: enrichedLineItem.metadata,
            productId: (_enrichedLineItem$pro = enrichedLineItem.productInfo) == null ? void 0 : _enrichedLineItem$pro.productId,
            seats: enrichedLineItem.seats
          };
        })
      });
      if (!updatedCart.cart) {
        // TODO - error handiling, if something was wrong in the response maybe try to getCart again
        throw new Error('No cart response in updateLineItems');
      }
      const {
        cart: updatedEnrichedCart
      } = await enrichCart({
        cart: updatedCart.cart
      });
      QueryUtils.setQueryData(CartApiClient, PremiumCartApiAggregations.getEnrichedCurrentCart.name, {
        cart: updatedEnrichedCart
      });
      return {
        cart: updatedEnrichedCart
      };
    }
  },
  updateLineItem: {
    name: 'updateLineItem',
    service,
    tags: [CartApiTags.cart, CartApiTags.updateLineItems],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref5 => {
      let {
        httpClient,
        request
      } = _ref5;
      const {
        lineItem
      } = request;
      const CartApiClient = await CartApiClientUtils.dynamicLoadCartApiClient();
      const {
        getEnrichedCurrentCart,
        updateLineItems
      } = CartApiClient.queries({
        httpClient
      });
      const {
        cart
      } = await getEnrichedCurrentCart({});
      if (!(cart != null && cart.id) || !cart.revision || !lineItem.id) {
        // TODO - error handling
        throw new Error('updateLineItem request params error');
      }
      const {
        cart: updatedEnrichedCart
      } = await updateLineItems({
        lineItems: {
          [lineItem.id]: lineItem
        }
      });
      QueryUtils.setQueryData(CartApiClient, PremiumCartApiAggregations.getEnrichedCurrentCart.name, {
        cart: updatedEnrichedCart
      });
      return {
        cart: updatedEnrichedCart
      };
    }
  },
  createOrderSession: {
    name: 'createOrderSession',
    queryConfigType: QueryConfigTypes.REALTIME_NO_REFETCH,
    service,
    queryFn: async _ref6 => {
      let {
        httpClient
      } = _ref6;
      const {
        getCurrentCart,
        createCheckout
      } = await CartApiClientUtils.dynamicLoadCartQueries(httpClient);
      const {
        cart
      } = await getCurrentCart({});
      if (!(cart != null && cart.id)) {
        // TODO - error handling
        throw new Error('No cart id');
      }
      const createCheckoutResponse = await createCheckout({
        cartId: cart.id
      });
      return createCheckoutResponse;
    }
  },
  getCartPricing: {
    name: 'getCartPricing',
    service,
    tags: [CartApiTags.cart],
    queryFn: queryCartPricing
  },
  applyActions: {
    name: 'applyActions',
    service,
    tags: [CartApiTags.cart, CartApiTags.applyActions],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref7 => {
      let {
        httpClient,
        request
      } = _ref7;
      const CartApiClient = await CartApiClientUtils.dynamicLoadCartApiClient();
      const {
        getCurrentCart,
        applyCartActions,
        enrichCart
      } = CartApiClient.queries({
        httpClient
      });
      const {
        cart
      } = await getCurrentCart({});
      const {
        id,
        revision
      } = cart ?? {};
      if (!id || !revision) {
        // TODO - error handling
        throw new Error('No cart id and no cart revision');
      }
      const {
        cart: cartWithAppliedActions
      } = await applyCartActions({
        cartId: id,
        cartRevision: revision,
        actions: request.actions
      });
      if (!cartWithAppliedActions) {
        // TODO - error handling
        throw new Error('applyActions no cart after apply');
      }
      const {
        cart: appliedActionsEnrichedCart
      } = await enrichCart({
        cart: cartWithAppliedActions
      });
      QueryUtils.setQueryData(CartApiClient, PremiumCartApiAggregations.getEnrichedCurrentCart.name, {
        cart: appliedActionsEnrichedCart
      });
      return {
        cart: appliedActionsEnrichedCart
      };
    }
  },
  removeLineItem: {
    name: 'removeLineItem',
    service,
    tags: [CartApiTags.cart, CartApiTags.removeLineItem],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref8 => {
      let {
        httpClient,
        request
      } = _ref8;
      const {
        lineItemId
      } = request;
      const CartApiClient = await CartApiClientUtils.dynamicLoadCartApiClient();
      const {
        getCurrentCart,
        removeLineItems,
        enrichCart
      } = CartApiClient.queries({
        httpClient
      });
      const {
        cart
      } = await getCurrentCart({});
      if (!(cart != null && cart.id) || !cart.revision) {
        // TODO - error handling
        throw new Error('removeLineItem request params error');
      }
      const {
        cart: cartAfterRemove
      } = await removeLineItems({
        cartId: cart.id,
        revision: cart.revision,
        lineItemIds: [lineItemId]
      });
      if (!cartAfterRemove) {
        // TODO - error handling
        throw new Error('removeLineItem no cart after remove');
      }
      const {
        cart: removedEnrichedCart
      } = await enrichCart({
        cart: cartAfterRemove
      });
      QueryUtils.setQueryData(CartApiClient, PremiumCartApiAggregations.getEnrichedCurrentCart.name, {
        cart: removedEnrichedCart
      });
      return {
        cart: removedEnrichedCart
      };
    }
  },
  upsertLineItemsToActiveCart: {
    name: 'upsertLineItemsToActiveCart',
    service,
    tags: [CartApiTags.cart],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref9 => {
      let {
        httpClient,
        request
      } = _ref9;
      return upsertLineItems(httpClient, request.lineItems);
    }
  },
  upsertLineItemToActiveCart: {
    name: 'upsertLineItemToActiveCart',
    service,
    tags: [CartApiTags.cart],
    isMutating: true,
    queryConfigType: QueryConfigTypes.MUTATION_METHOD,
    queryFn: async _ref10 => {
      let {
        httpClient,
        request
      } = _ref10;
      return upsertLineItems(httpClient, request.lineItem ? [request.lineItem] : []);
    }
  }
};
//# sourceMappingURL=premium-cart-aggregations.api-config.js.map